'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Assignment,
  AssignmentTurnedIn,
  NotificationsActive,
  BarChart,
  TrendingUp,
  PhoneAndroid,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  Link as LinkIcon,
  People,
  FormatListBulleted,
  FolderOpen,
  Edit,
  Groups,
  Download as DownloadIcon,
  Settings,
  AltRoute,
  Category,
  AssignmentInd,
  Save,
  AddAPhoto,
  TrackChanges,
  Bolt,
  Dashboard,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';
import WorkflowStepper from '../../components/WorkflowStepper';
import BrandName from '../../components/BrandName';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Build the Tool', description: 'Create audit checklists in the Form Wizard with sections, skip logic, files, and signatures' },
  { title: 'Assign', description: 'Schedule an audit per facility with category, type, priority, due date, and an assigned user, role, or committee' },
  { title: 'Complete', description: 'Staff work audits from My Audits on web or mobile, saving drafts before submitting' },
  { title: 'Measure', description: 'Track yes/no compliance rates against KPI targets on the audit dashboard' },
  { title: 'Act', description: 'Link audits to action plans, and configure KPI triggers for corrective work' }
];

const painPoints = [
  'Audit checklists photocopied, completed on clipboards, and filed unread',
  'No assignment or due dates—audits happen when someone remembers',
  'Results never tallied, so compliance rates and trends are invisible',
  'No targets, so "how are we doing?" has no honest answer',
  'Findings that never become corrective action plans',
  'Paper trails that can\'t demonstrate an active monitoring program during survey',
];

const capabilities = [
  {
    icon: Settings,
    title: 'Configurable Audit Tools',
    description: 'Build audit checklists in the Form Wizard with multi-section structure and rich question types—text, numeric, select, radio and checkbox grids, tabular grids, dates, ratings, file uploads, instructions, and signatures—shaped to your audit program, not a vendor\'s template.',
  },
  {
    icon: AltRoute,
    title: 'Skip Logic & Focused Audits',
    description: 'Add skip logic so auditors only answer what applies, and assign a single form section when you want a focused audit instead of the full tool.',
  },
  {
    icon: Category,
    title: 'Categories, Types & Priorities',
    description: 'Organize audits with configurable categories and types—your clinical, environmental, dietary, and administrative structure—and prioritize each assignment as high, medium, or low.',
  },
  {
    icon: AssignmentInd,
    title: 'Assignment to User, Role, or Committee',
    description: 'Assign each audit to a specific user, an entire role, or a committee, with a due date. Audits become owned, scheduled work instead of background intentions.',
  },
  {
    icon: Assignment,
    title: 'My Audits Workqueue',
    description: 'Assignees see their audits in New, In Progress, and Completed queues—filterable by priority, category, and type—and complete them in the platform\'s standard form experience, with new audits also surfacing on the main dashboard.',
  },
  {
    icon: Save,
    title: 'Draft & Complete Lifecycle',
    description: 'Auditors save work in progress and return to it later. Submissions record who responded and when, and submitted answers are reviewable read-only afterward.',
  },
  {
    icon: AddAPhoto,
    title: 'Evidence Capture',
    description: 'Attach photos and files and capture signatures directly in the audit form—evidence lives with the response, not in a separate folder.',
  },
  {
    icon: TrackChanges,
    title: 'KPI Targets & Compliance Rates',
    description: 'Mark the questions that matter and CareAscendium computes yes/no compliance percentages per audit. Define KPI targets with comparison operators and frequencies, and see at a glance which audits meet target and which don\'t.',
  },
  {
    icon: Dashboard,
    title: 'Audit Dashboard',
    description: 'Visualize KPI performance with configurable chart types, grouped by audit and filtered by facility—compliance trending leadership can read in seconds.',
  },
  {
    icon: Bolt,
    title: 'Action Plan & PIP Triggers',
    description: 'Link audits to corrective action plans, and configure KPI triggers that generate an action plan or performance improvement plan—with a name, due window, and assignee—when results miss target.',
  },
  {
    icon: PhoneAndroid,
    title: 'Mobile Audits',
    description: 'Complete audits from the CareAscendium mobile app—facility picker, status tabs, filters, full form entry, and submitted-answer review—so audits happen where care happens.',
  },
];

const roles = [
  {
    title: 'Compliance & quality leaders',
    benefit: 'One audit program with tools, assignments, completion status, and KPI trends',
  },
  {
    title: 'Administrators & DONs',
    benefit: 'Dashboard visibility into compliance rates against targets across audit areas',
  },
  {
    title: 'Department auditors',
    benefit: 'A clear queue of assigned audits with due dates, drafts, and mobile completion',
  },
  {
    title: 'QAPI teams',
    benefit: 'Audit results feeding KPI thresholds that trigger action plans and PIPs',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Standardized audit tools and a consistent monitoring program across facilities',
  },
];

const outcomes = [
  { value: 'Monitored', label: 'Compliance rates', detail: 'Yes/no compliance percentages per audit, tracked over time' },
  { value: 'Targeted', label: 'Target performance', detail: 'Which KPIs meet target and which miss, with configurable thresholds' },
  { value: 'Completed', label: 'Completion discipline', detail: 'New, in-progress, and completed audits with due dates and submitters' },
  { value: 'Assigned', label: 'Clear ownership', detail: 'Every audit assigned to a user, role, or committee' },
  { value: 'Resolved', label: 'Corrective follow-up', detail: 'Audits linked to action plans, with KPI trigger rules for missed targets' },
  { value: 'Standard', label: 'Program consistency', detail: 'Standardized audit tools across units and facilities' },
];

const integrations = [
  'Form Wizard',
  'Action Plans',
  'Performance Improvement Plans',
  'Committees',
  'Dashboards',
  'Multi-Facility Management',
];

const faqs = [
  {
    q: 'Can we build our own audit tools?',
    a: 'Yes. Audit checklists are built in the Form Wizard with your sections and question types—including grids, ratings, file uploads, and signatures—plus skip logic so auditors only answer what applies.',
  },
  {
    q: 'Who can audits be assigned to?',
    a: 'A specific user, an entire role, or a committee—each assignment with a category, type, priority, and due date. Assignees work from their My Audits queue on web or mobile.',
  },
  {
    q: 'How is compliance measured?',
    a: 'Mark the questions to track and CareAscendium computes yes/no compliance percentages per audit. Define KPI targets with comparison operators, and the dashboard shows which audits meet target.',
  },
  {
    q: 'What happens when an audit misses its target?',
    a: 'Audits can be linked to corrective action plans, and KPI triggers can be configured to generate an action plan or PIP—with an assignee and due window—when results miss target.',
  },
  {
    q: 'Can auditors save partial work?',
    a: 'Yes. Audits support an in-progress draft state, and completed submissions are reviewable read-only with the submitter and completion date recorded.',
  },
  {
    q: 'Does it work on mobile?',
    a: 'Yes. The CareAscendium mobile app supports the full assignee workflow—status tabs, filters, form entry with attachments, and submitted-answer review—on iOS and Android.',
  },
  {
    q: 'Can we run a focused audit instead of the whole tool?',
    a: 'Yes. Assign a single section of an audit form when you want a targeted spot-check rather than the full audit.',
  },
];

export default function AuditManagementClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Audit Management Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Audit Management"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            Build, Assign, and Complete Every Audit—Then Watch Compliance Rates Against Targets
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives skilled nursing facilities a complete internal audit workflow—configurable audit tools with skip logic, files, and signatures; assignment to users, roles, or committees with due dates; web and mobile completion; and KPI dashboards that show compliance rates against the targets you set.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Clipboard Audits
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Internal audits are how facilities catch problems before surveyors do—but in most SNFs, audits live on photocopied checklists. Completed forms pile up in folders nobody reviews, results are never trended, and the same deficiencies persist month after month because no number ever told leadership things were slipping.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;An audit program that doesn't produce a number can't tell you whether you're getting better or worse.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Workflow. From Audit Tool to Compliance Trend.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> digitizes the entire audit cycle. Build audit tools as configurable forms, assign them to the right people with due dates and priorities, let staff complete them on web or mobile with drafts along the way, and track yes/no compliance rates against KPI targets on visual dashboards—with triggers that turn missed targets into action plans and improvement projects.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Audit Management plugs your monitoring program into the rest of your quality system:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Monitoring that produces numbers, not paper.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Documentation You Can Stand Behind
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
            <BrandName variant="light" style={{ fontSize: 'inherit' }} /> supports the active monitoring program surveyors expect to see:
          </Typography>
          <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>A defined audit program with configurable tools, categories, and types</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Assigned, due-dated audits with named accountability</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Structured responses with comments, files, and signatures as evidence</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Compliance-rate KPIs measured against explicit targets</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Audit-linked corrective action plans closing the loop on findings</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>A complete completion history—who audited what, and when</Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 3, pt: 0 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontSize: '0.95rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Retire the Clipboard?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> turns internal audits into a measurable monitoring program—assigned, completed, trended against targets, and connected to corrective action.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
